import { buildPublicationChangeSet } from "../changeSet";
import { areDatesEqualToMinute, deepEqual, pubsEqual, snapshotPub } from "./compare";
import { isImmutablePub } from "./status";
import type { DirtyState, LocalSessionState, PublicationModel, SchedulingSession } from "./types";

function hasConfigChanged(session: SchedulingSession): boolean {
  if (!areDatesEqualToMinute(session.config.startFrom, session.entryConfig.startFrom)) return true;
  if (!deepEqual(session.config.preferredHours, session.entryConfig.preferredHours)) return true;
  if (session.accountCadences.size !== session.entryAccountCadences.size) return true;
  for (const [accountId, cadence] of session.accountCadences) {
    if (!deepEqual(cadence, session.entryAccountCadences.get(accountId))) return true;
  }
  return false;
}

function hasAccountSelectionChanged(session: SchedulingSession): boolean {
  const entrySet = new Set(session.entryState.selectedAccountIds);
  const localSet = new Set(session.local.selectedAccountIds);
  return entrySet.size !== localSet.size || [...entrySet].some((id) => !localSet.has(id));
}

/** Persistable controls compared with the clean entry snapshot. */
export function hasSessionConfigChanged(session: SchedulingSession): boolean {
  return hasConfigChanged(session) || hasAccountSelectionChanged(session);
}

/** The exact server delta, shared by CTA and save preview. Opening drafts
 * belong here even though they do not make the entry-relative diff dirty. */
export function computeSessionPublicationChangeSet(session: SchedulingSession) {
  return buildPublicationChangeSet({
    orderedVideos: session.local.orderedVideoIds.map((id) => ({ id })),
    perVideoStateById: session.local.perVideoState,
    baselinePublications: session.baseline.publications,
    editableVideoScopeIdSet: new Set([
      ...session.baseline.orderedVideoIds,
      ...session.entryState.orderedVideoIds,
      ...session.local.orderedVideoIds,
    ]),
    isSingleVideoPublishNow: false,
    areOptionalDatesEqualToMinute: areDatesEqualToMinute,
    areDeepValuesEqual: deepEqual,
  });
}

/** Persisted identity is never collapsed by account. Unsaved drafts use
 * their destination identity so removing/re-adding an identical draft is a
 * net-zero edit even if materialization allocated a new temporary id. */
function entryDiffRows(local: LocalSessionState, existingIds: Set<string>) {
  const rows = new Map<string, { videoId: string; publication: PublicationModel }>();
  for (const videoId of local.orderedVideoIds) {
    for (const publication of local.perVideoState[videoId]?.localPublications ?? []) {
      const key = existingIds.has(publication.id)
        ? `persisted:${publication.id}`
        : `draft:${videoId}:${publication.accountId}`;
      rows.set(key, { videoId, publication });
    }
  }
  return rows;
}

export function computeDirtyState(session: SchedulingSession): DirtyState {
  const serverDelta = computeSessionPublicationChangeSet(session);
  const entryRows = entryDiffRows(session.entryState, session.existingPublicationIds);
  const localRows = entryDiffRows(session.local, session.existingPublicationIds);
  const dirtyVideoIds = new Set<string>();
  const autoGenRemovals: string[] = [];
  let createdCount = 0;
  let updatedCount = 0;
  let removedCount = 0;
  let persistedRemovedCount = 0;

  for (const key of new Set([...entryRows.keys(), ...localRows.keys()])) {
    const entry = entryRows.get(key);
    const local = localRows.get(key);
    const entryPub = entry?.publication;
    const localPub = local?.publication;
    if ((entryPub && isImmutablePub(entryPub)) || (localPub && isImmutablePub(localPub))) continue;
    if (pubsEqual(snapshotPub(entryPub), snapshotPub(localPub)) &&
      entryPub?.accountId === localPub?.accountId && entry?.videoId === local?.videoId) continue;

    const videoId = local?.videoId ?? entry!.videoId;
    if (!localPub && entryPub && !session.existingPublicationIds.has(entryPub.id)) {
      autoGenRemovals.push(videoId);
      continue;
    }
    dirtyVideoIds.add(videoId);
    if (!localPub) {
      removedCount++;
      persistedRemovedCount++;
    } else if (!entryPub) {
      createdCount++;
    } else {
      updatedCount++;
    }
  }

  const intentChanged = hasConfigChanged(session) || hasAccountSelectionChanged(session) ||
    !deepEqual(session.entryState.videoAccountOverrides, session.local.videoAccountOverrides) ||
    !deepEqual(session.entryState.orderedVideoIds, session.local.orderedVideoIds) ||
    session.customDatetimeKeys.size !== session.entryCustomDatetimeKeys.size ||
    [...session.customDatetimeKeys].some((key) => !session.entryCustomDatetimeKeys.has(key));
  // Discarding opening drafts alone has no server effect. Keep that state
  // clean, but include the discarded rows in a larger real edit's summary.
  if (dirtyVideoIds.size > 0 || intentChanged || serverDelta.totalCount > 0) {
    for (const videoId of autoGenRemovals) {
      dirtyVideoIds.add(videoId);
      removedCount++;
    }
  }

  const totalCount = createdCount + updatedCount + removedCount;
  const parts: string[] = [];
  if (updatedCount > 0) parts.push(`${updatedCount} updated`);
  if (createdCount > 0) parts.push(`${createdCount} created`);
  if (removedCount > 0) parts.push(`${removedCount} removed`);
  return {
    isDirty: dirtyVideoIds.size > 0 || intentChanged,
    dirtyVideoIds,
    changeSet: { createdCount, updatedCount, removedCount, persistedRemovedCount, totalCount },
    serverChangeSet: {
      createdCount: serverDelta.createdCount,
      updatedCount: serverDelta.editedCount,
      removedCount: serverDelta.removedCount,
      totalCount: serverDelta.totalCount,
    },
    changeSummary: parts.length > 0 ? parts.join(", ") : null,
  };
}
